import React, { useState, useEffect } from 'react';
import { RefreshCw, Eye, CheckCircle, Trash2, Mail, Zap, Filter, Pause, Settings, Plus, FlaskConical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OutboxInbox() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [manualCreateOpen, setManualCreateOpen] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = filter 
        ? `${API_URL}/api/outbox/inbox?status=${filter}`
        : `${API_URL}/api/outbox/inbox`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Error al cargar bandeja');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/outbox/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Generados: ${response.data.stats.total} mensajes nuevos`);
      fetchMessages();
    } catch (error) {
      console.error('Error generating messages:', error);
      toast.error('Error al generar mensajes');
    } finally {
      setGenerating(false);
    }
  };

  const handleLoadDemoData = async () => {
    if (!window.confirm('¿Cargar datos de prueba? Esto creará clientes y pólizas ficticias.')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/debug/seed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `🧪 Datos demo creados: ${response.data.data.clients} clientes, ${response.data.data.policies} pólizas`,
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Error loading demo data:', error);
      toast.error('Error al cargar datos demo');
    } finally {
      setLoading(false);
    }
  };

  const handleViewEdit = async (message) => {
    setSelectedMessage(message);
    setEditMode(false);
    setPreviewOpen(true);
  };

  const handleApproveQuick = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/outbox/inbox/${messageId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Mensaje aprobado');
      fetchMessages();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Error al aprobar');
    }
  };

  const handleRevertToDraft = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/outbox/inbox/${messageId}/revert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('⏸️ Mensaje revertido a borrador');
      fetchMessages();
    } catch (error) {
      console.error('Error reverting:', error);
      toast.error('Error al revertir');
    }
  };

  const handleSkip = async (messageId) => {
    if (!window.confirm('¿Descartar este mensaje? No se enviará.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/outbox/inbox/${messageId}/skip`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('🗑️ Mensaje descartado');
      fetchMessages();
    } catch (error) {
      console.error('Error skipping:', error);
      toast.error('Error al descartar');
    }
  };

  const handleSaveMessage = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/outbox/inbox/${selectedMessage.id}`,
        {
          final_subject: selectedMessage.final_subject,
          final_html_body: selectedMessage.final_html_body
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Cambios guardados');
      setEditMode(false);
      fetchMessages();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar cambios');
    }
  };

  const handleApproveAndClose = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Guardar cambios si está en modo edición
      if (editMode) {
        await axios.put(
          `${API_URL}/api/outbox/inbox/${selectedMessage.id}`,
          {
            final_subject: selectedMessage.final_subject,
            final_html_body: selectedMessage.final_html_body
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Aprobar
      await axios.post(
        `${API_URL}/api/outbox/inbox/${selectedMessage.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Mensaje guardado y aprobado');
      setPreviewOpen(false);
      fetchMessages();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar mensaje');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      DRAFT: { label: 'Borrador', className: 'bg-yellow-100 text-yellow-800' },
      APPROVED: { label: 'Aprobado', className: 'bg-green-100 text-green-800' },
      SENT: { label: 'Enviado', className: 'bg-blue-100 text-blue-800' },
      SKIPPED: { label: 'Descartado', className: 'bg-gray-100 text-gray-800' }
    };
    const c = config[status] || config.DRAFT;
    return <span className={`px-2 py-1 text-xs rounded-full ${c.className}`}>{c.label}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Mail className="h-8 w-8 text-indigo-600" />
              Bandeja de Salida
            </h1>
            <p className="text-gray-600 mt-2">
              Revisa y aprueba los mensajes generados automáticamente
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleLoadDemoData}
              variant="outline"
              size="lg"
              className="gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              <FlaskConical className="h-5 w-5" />
              🧪 Cargar Datos Demo
            </Button>
            <Button
              onClick={() => navigate('/email-settings')}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              <Settings className="h-5 w-5" />
              Configuración
            </Button>
            <Button
              onClick={() => setManualCreateOpen(true)}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              <Plus className="h-5 w-5" />
              Nuevo Email Manual
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="gap-2"
              size="lg"
            >
              {generating ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5" />
                  Generar Envíos
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filtrar:</span>
          <div className="flex gap-2">
            <Button
              onClick={() => setFilter('')}
              variant={filter === '' ? 'default' : 'outline'}
              size="sm"
            >
              Todos ({messages.length})
            </Button>
            <Button
              onClick={() => setFilter('DRAFT')}
              variant={filter === 'DRAFT' ? 'default' : 'outline'}
              size="sm"
            >
              Borradores
            </Button>
            <Button
              onClick={() => setFilter('APPROVED')}
              variant={filter === 'APPROVED' ? 'default' : 'outline'}
              size="sm"
            >
              Aprobados
            </Button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            Cargando mensajes...
          </div>
        ) : messages.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg mb-2">No hay mensajes pendientes</p>
            <p className="text-slate-400 text-sm">
              Haz clic en "Generar Envíos" para crear mensajes automáticamente
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Destinatario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Motivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Fecha Sugerida
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {messages.map((msg) => (
                <tr key={msg.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">
                      {msg.recipient_name}
                    </div>
                    <div className="text-sm text-slate-500">
                      {msg.key_data}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-700">
                      {msg.message_reason || 'Sin motivo'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {msg.template_type ? msg.template_type.replace('_', ' ') : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {new Date(msg.scheduled_at).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(msg.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1.5">
                      {/* Ver/Editar - siempre visible */}
                      <Button
                        onClick={() => handleViewEdit(msg)}
                        variant="outline"
                        size="sm"
                        title="Ver/Editar"
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {/* Aprobar - solo para DRAFT */}
                      {msg.status === 'DRAFT' && (
                        <Button
                          onClick={() => handleApproveQuick(msg.id)}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 hover:text-green-700"
                          title="Aprobar"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {/* Pausar/Revertir - solo para APPROVED */}
                      {msg.status === 'APPROVED' && (
                        <Button
                          onClick={() => handleRevertToDraft(msg.id)}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                          title="Pausar/Revertir a Borrador"
                        >
                          <Pause className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {/* Descartar - visible para DRAFT y APPROVED */}
                      {(msg.status === 'DRAFT' || msg.status === 'APPROVED') && (
                        <Button
                          onClick={() => handleSkip(msg.id)}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                          title="Descartar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de Preview/Edición */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold">
                    {selectedMessage?.recipient_name}
                  </div>
                  <div className="text-sm font-normal text-slate-500">
                    {selectedMessage?.message_reason} - {selectedMessage?.key_data}
                  </div>
                </div>
                <Button
                  onClick={() => setEditMode(!editMode)}
                  variant="outline"
                  size="sm"
                >
                  {editMode ? 'Vista Previa' : 'Editar'}
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4">
            {editMode ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Asunto:
                  </label>
                  <input
                    type="text"
                    value={selectedMessage?.final_subject || ''}
                    onChange={(e) => setSelectedMessage({
                      ...selectedMessage,
                      final_subject: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contenido (HTML):
                  </label>
                  <textarea
                    value={selectedMessage?.final_html_body || ''}
                    onChange={(e) => setSelectedMessage({
                      ...selectedMessage,
                      final_html_body: e.target.value
                    })}
                    rows={15}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">Asunto:</div>
                  <div className="text-base text-slate-900 p-3 bg-slate-50 rounded-lg">
                    {selectedMessage?.final_subject}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">Preview:</div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={selectedMessage?.final_html_body}
                      title="Email Preview"
                      className="w-full"
                      sandbox="allow-same-origin"
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={() => setPreviewOpen(false)} variant="outline">
              Cerrar
            </Button>
            {editMode && (
              <Button onClick={handleSaveMessage}>
                Guardar Cambios
              </Button>
            )}
            {selectedMessage?.status === 'DRAFT' && (
              <Button onClick={handleApproveAndClose} className="gap-2">
                <CheckCircle className="h-4 w-4" />
                Guardar y Aprobar
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Creación Manual */}
      <ManualEmailCreateModal
        isOpen={manualCreateOpen}
        onClose={() => setManualCreateOpen(false)}
        onSuccess={() => {
          setManualCreateOpen(false);
          fetchMessages();
        }}
      />
    </div>
  );
}

// Componente Modal de Creación Manual
function ManualEmailCreateModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchTemplates();
      setStep(1);
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/clients`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/templates/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    if (template && selectedClient) {
      // Renderizar con datos básicos del cliente
      const data = {
        nombre: selectedClient.name || '',
        email: selectedClient.email || '',
        telefono: selectedClient.phone || ''
      };
      
      // Simple replacement (el backend lo hará mejor con Jinja2)
      let renderedSubject = template.subject_template || '';
      let renderedBody = template.body_html_template || '';
      
      Object.keys(data).forEach(key => {
        renderedSubject = renderedSubject.replace(new RegExp(`{{${key}}}`, 'g'), data[key]);
        renderedBody = renderedBody.replace(new RegExp(`{{${key}}}`, 'g'), data[key]);
      });
      
      setSubject(renderedSubject);
      setBody(renderedBody);
    }
  };

  const handleCreate = async () => {
    if (!selectedClient || !subject || !body) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const messageData = {
        client_id: selectedClient.id,
        recipient_email: selectedClient.email,
        recipient_name: selectedClient.name,
        template_type: selectedTemplate?.type || 'manual',
        template_id: selectedTemplate?.id || null,
        message_reason: 'Email manual',
        key_data: 'Creado manualmente',
        final_subject: subject,
        final_html_body: body,
        scheduled_at: scheduledDate || new Date().toISOString(),
        status: 'DRAFT'
      };

      await axios.post(
        `${API_URL}/api/outbox/inbox/manual`,
        messageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('✅ Email creado exitosamente');
      onSuccess();
    } catch (error) {
      console.error('Error creating manual email:', error);
      toast.error('Error al crear email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Crear Email Manual - Paso {step} de 3
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {step === 1 && (
            <div>
              <h3 className="font-medium text-slate-900 mb-3">1. Selecciona un Cliente</h3>
              <div className="max-h-96 overflow-auto border border-slate-200 rounded-lg">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => {
                      setSelectedClient(client);
                      setStep(2);
                    }}
                    className={`p-3 cursor-pointer hover:bg-slate-50 border-b ${
                      selectedClient?.id === client.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium">{client.name}</div>
                    <div className="text-sm text-slate-500">{client.email}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="font-medium text-slate-900 mb-3">2. Elige una Plantilla (o crea desde cero)</h3>
              <div className="space-y-2 mb-4">
                <Button
                  onClick={() => {
                    setSelectedTemplate(null);
                    setSubject('');
                    setBody('');
                    setStep(3);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  📝 Email en Blanco
                </Button>
              </div>
              <div className="max-h-80 overflow-auto space-y-2">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => {
                      handleSelectTemplate(template);
                      setStep(3);
                    }}
                    className="p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{template.icon}</span>
                      <div>
                        <div className="font-medium">{template.name}</div>
                        <div className="text-sm text-slate-500">{template.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <div className="text-sm text-slate-500 mb-2">
                  Para: <strong>{selectedClient?.name}</strong> ({selectedClient?.email})
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Asunto: *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Escribe el asunto del email"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Contenido (HTML): *
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  placeholder="<p>Escribe el contenido del email aquí...</p>"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fecha de Envío (opcional):
                </label>
                <input
                  type="datetime-local"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 pt-4 border-t">
          <div>
            {step > 1 && (
              <Button onClick={() => setStep(step - 1)} variant="outline">
                ← Atrás
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline">
              Cancelar
            </Button>
            {step === 3 && (
              <Button onClick={handleCreate} disabled={loading}>
                {loading ? 'Creando...' : 'Crear Borrador'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
